import { createContext, useContext, useCallback, useState, useRef } from 'react';
import { api } from '../../../services/api';
import { useAdminUsers } from '../../manageAdmin/context/AdminUsersContext';
import { AdminPermission } from '../../manageAdmin/AdminUserList.types';
import type {
  Blog,
  BlogMutationInput,
  CompleteBlogClinicalReviewInput,
  CompleteBlogClinicalReviewResponse,
  GetBlogsParams,
  UpdateBlogResponse,
} from '../Blog.types';
import { ShowToast } from '../../../services/toast';
import { getBlogErrorMessage, isCanceledBlogRequest } from '../blog-errors';

type BlogContextType = {
  blogs: Blog[];
  draftBlogs: Blog[];
  total: number;
  draftCount: number;
  loading: boolean;
  draftLoading: boolean;
  fetchBlogs: (params?: GetBlogsParams) => Promise<void>;
  fetchDrafts: (params?: Omit<GetBlogsParams, 'type'>) => Promise<void>;
  createBlog: (blog: BlogMutationInput, action?: 'publish' | 'draft' | 'schedule') => Promise<Blog>;
  updateBlog: (
    id: string,
    blog: BlogMutationInput,
    action?: 'publish' | 'draft' | 'schedule'
  ) => Promise<UpdateBlogResponse>;
  completeClinicalReview: (
    id: string,
    review: CompleteBlogClinicalReviewInput
  ) => Promise<CompleteBlogClinicalReviewResponse>;
  deleteBlog: (id: string) => Promise<void>;
};

const BlogContext = createContext<BlogContextType | undefined>(undefined);

const showSuccessMsg = (action: 'publish' | 'draft' | 'schedule', type: 'success' | 'error') => {
  let message = '';
  if (action === 'publish') {
    message = 'Resource published successfully';
  }
  if (action === 'draft') {
    message = 'Resource draft saved successfully';
  }
  if (action === 'schedule') {
    message = 'Resource scheduled successfully';
  }
  ShowToast({
    type: type,
    message: message,
  });
};

export function BlogProvider({ children }: { children: React.ReactNode }) {
  const { myPermissions } = useAdminUsers();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [draftBlogs, setDraftBlogs] = useState<Blog[]>([]);
  const [total, setTotal] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const signalRef = useRef<AbortController | null>(null);
  const draftSignalRef = useRef<AbortController | null>(null);

  const fetchBlogs = useCallback(
    async (params?: GetBlogsParams) => {
      if (myPermissions?.includes(AdminPermission.READ_PAGE_MANAGE_BLOG)) {
        try {
          if (signalRef.current) {
            signalRef.current.abort();
          }

          const controller = new AbortController();
          signalRef.current = controller;

          setLoading(true);
          const response = await api.getBlogs(
            {
              page: params?.page || 0,
              limit: params?.limit || 20,
              keyword: params?.keyword,
              type: params?.type || 'all',
              sort: params?.sort,
              sortAsc: params?.sortAsc,
              topicIds: params?.topicIds,
              audience: params?.audience,
              contentType: params?.contentType,
              collection: params?.collection,
            },
            controller.signal
          );

          if (signalRef.current === controller) {
            setBlogs(response.blogs);
            setTotal(response.total);
            setDraftCount(response.draftCount);
            setLoading(false);
          }
        } catch (error: unknown) {
          if (!isCanceledBlogRequest(error)) {
            ShowToast({
              type: 'error',
              message: getBlogErrorMessage(error, 'Failed to fetch Resources'),
            });
            setLoading(false);
          }
        }
      }
    },
    [myPermissions]
  );

  const fetchDrafts = useCallback(
    async (params?: Omit<GetBlogsParams, 'type'>) => {
      if (myPermissions?.includes(AdminPermission.READ_PAGE_MANAGE_BLOG)) {
        try {
          if (draftSignalRef.current) {
            draftSignalRef.current.abort();
          }

          const controller = new AbortController();
          draftSignalRef.current = controller;

          setDraftLoading(true);
          const response = await api.getBlogs(
            {
              page: params?.page || 0,
              limit: params?.limit || 20,
              keyword: params?.keyword,
              type: 'draft',
              sort: params?.sort,
              sortAsc: params?.sortAsc,
            },
            controller.signal
          );

          if (draftSignalRef.current === controller) {
            setDraftBlogs(response.blogs);
            setDraftLoading(false);
          }
        } catch (error: unknown) {
          if (!isCanceledBlogRequest(error)) {
            ShowToast({
              type: 'error',
              message: getBlogErrorMessage(error, 'Failed to fetch drafts'),
            });
            setDraftLoading(false);
          }
        }
      }
    },
    [myPermissions]
  );

  const createBlog = useCallback(
    async (blog: BlogMutationInput, action?: 'publish' | 'draft' | 'schedule') => {
      try {
        setLoading(true);
        const newBlog = await api.createBlog(blog);
        showSuccessMsg(action || 'draft', 'success');
        return newBlog;
      } catch (error: unknown) {
        const errorMessage = getBlogErrorMessage(error);
        ShowToast({
          type: 'error',
          message: errorMessage || 'Failed to create Resource',
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  ); // Removed fetchBlogs from dependencies

  const updateBlog = useCallback(
    async (id: string, blog: BlogMutationInput, action?: 'publish' | 'draft' | 'schedule') => {
      try {
        setLoading(true);
        const response = await api.updateBlog(id, blog);
        showSuccessMsg(action || 'draft', 'success');
        return response;
      } catch (error: unknown) {
        const errorMessage = getBlogErrorMessage(error);
        ShowToast({
          type: 'error',
          message: errorMessage || 'Failed to update Resource',
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  ); // Removed fetchBlogs from dependencies

  const deleteBlog = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        await api.deleteBlog(id);
        ShowToast({
          type: 'success',
          message: 'Resource deleted successfully',
        });
      } catch (error: unknown) {
        ShowToast({
          type: 'error',
          message: getBlogErrorMessage(error, 'Failed to delete Resource'),
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchBlogs]
  );

  const completeClinicalReview = useCallback(
    async (id: string, review: CompleteBlogClinicalReviewInput) => {
      try {
        setLoading(true);
        const response = await api.completeBlogClinicalReview(id, review);
        ShowToast({
          type: 'success',
          message: 'Clinical review recorded successfully',
        });
        return response;
      } catch (error: unknown) {
        ShowToast({
          type: 'error',
          message: getBlogErrorMessage(error, 'Failed to record clinical review'),
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return (
    <BlogContext.Provider
      value={{
        blogs,
        draftBlogs,
        total,
        draftCount,
        loading,
        draftLoading,
        fetchBlogs,
        fetchDrafts,
        createBlog,
        updateBlog,
        completeClinicalReview,
        deleteBlog,
      }}
    >
      {children}
    </BlogContext.Provider>
  );
}

export function useBlogs() {
  const context = useContext(BlogContext);
  if (context === undefined) {
    throw new Error('useBlogs must be used within a BlogProvider');
  }
  return context;
}
